import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, apiScopes } from '@/auth/msalConfig';
const BASE_URL = '/api';

// Reuse the MSAL instance from AuthProvider if available, otherwise create one.
// In practice this module is loaded after AuthProvider has initialized,
// so we import the shared instance.
let msalInstance: PublicClientApplication | null = null;

function getMsalInstance(): PublicClientApplication {
  if (!msalInstance) {
    // Lazy-import the shared instance created in AuthProvider
    // This avoids circular dependency issues at module load time
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

/**
 * Set the MSAL instance to use for API calls.
 * Called by AuthProvider after initialization.
 */
export function setMsalInstance(instance: PublicClientApplication): void {
  msalInstance = instance;
}

/**
 * Acquire an access token for API calls.
 */
export async function getAccessToken(): Promise<string> {
  const instance = getMsalInstance();
  const accounts = instance.getAllAccounts();

  if (accounts.length === 0) {
    throw new Error('No authenticated account found.');
  }

  const account = instance.getActiveAccount() ?? accounts[0];

  try {
    const response = await instance.acquireTokenSilent({
      scopes: apiScopes,
      account,
    });
    return response.accessToken;
  } catch {
    // Silent token acquisition failed; fall back to interactive popup
    const response = await instance.acquireTokenPopup({
      scopes: apiScopes,
      account,
    });
    return response.accessToken;
  }
}

interface RequestOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body?: unknown,
  ) {
    super(`API Error ${status}: ${statusText}`);
    this.name = 'ApiError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestOptions,
  _isRetry = false,
): Promise<T> {
  const token = await getAccessToken();

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...options?.headers,
  };

  if (body !== undefined && body !== null) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined && body !== null ? JSON.stringify(body) : undefined,
    signal: options?.signal,
  });

  // SEC-009: On 401, retry once with a fresh token (triggers popup fallback if needed)
  if (response.status === 401 && !_isRetry) {
    return request<T>(method, path, body, options, true);
  }

  if (!response.ok) {
    let errorBody: unknown;
    try {
      errorBody = await response.json();
    } catch {
      // Response body is not JSON
    }
    throw new ApiError(response.status, response.statusText, errorBody);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  const json = await response.json();

  // Backend wraps all responses in { success, data }. Unwrap the data field
  // so callers receive the payload directly.
  if (json && typeof json === 'object' && 'data' in json) {
    return json.data as T;
  }

  return json as T;
}

export const apiClient = {
  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('GET', path, undefined, options);
  },

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('POST', path, body, options);
  },

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PUT', path, body, options);
  },

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PATCH', path, body, options);
  },

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('DELETE', path, undefined, options);
  },
};

export { ApiError };
